import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {
    Table, TableBody, TableCell, TableHead, TablePagination, TableRow, useMediaQuery, useTheme,
} from '@mui/material';
import ReportFilter from './components/ReportFilter';
import {useTranslation} from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePersistedState from '../common/util/usePersistedState';
import PositionValue from '../common/components/PositionValue';
import ColumnSelect from './components/ColumnSelect';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import {useCatch} from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';

const RouteReportPage = () => {
    const classes = useReportStyles();
    const theme = useTheme();
    const t = useTranslation();

    const phone = useMediaQuery(theme.breakpoints.down('md'));

    const positionAttributes = usePositionAttributes(t);

    const devices = useSelector((state) => state.devices.items);

    const [columns, setColumns] = usePersistedState('routeColumns', ["serverTime", "io11", "io12", "io13", "io14", "io15", "io16", "io17"]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    //
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(15);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };
    //

    const onMapPointClick = useCallback((positionId) => {
        setSelectedItem(items.find((it) => it.id === positionId));
    }, [items, setSelectedItem]);

    const handleSubmit = useCatch(async ({deviceIds, from, to, type}) => {
        const query = new URLSearchParams({from, to});
        deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
        if (type === 'export') {
            window.location.assign(`/api/reports/route/xlsx?${query.toString()}`);
        } else if (type === 'mail') {
            const response = await fetch(`/api/reports/route/mail?${query.toString()}`);
            if (!response.ok) {
                throw Error(await response.text());
            }
        } else {
            setLoading(true);
            try {
                const response = await fetch(`/api/reports/route?${query.toString()}`, {
                    headers: {Accept: 'application/json'},
                });
                if (response.ok) {
                    setItems(await response.json());
                } else {
                    throw Error(await response.text());
                }
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <PageLayout menu={<ReportsMenu/>} breadcrumbs={['reportTitle', 'reportRoute']}>
            <div className={classes.container}>
                <div className={classes.containerMain}>
                    <div className={classes.header}>
                        <ReportFilter handleSubmit={handleSubmit} multiDevice>
                            <ColumnSelect
                                columns={columns}
                                setColumns={setColumns}
                                columnsObject={positionAttributes}
                            />
                        </ReportFilter>
                    </div>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>{t('positionHashTag')}</b></TableCell>
                                <TableCell><b>{t('sharedDevice')}</b></TableCell>
                                {columns.map((key) => (
                                    <TableCell key={key}><b>{positionAttributes[key].name}</b></TableCell>))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading ? items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        {item.id}
                                    </TableCell>
                                    <TableCell>{devices[item.deviceId].name}</TableCell>
                                    {columns.map((key) => (
                                        <TableCell key={key}>
                                            <PositionValue
                                                position={item}
                                                property={item.hasOwnProperty(key) ? key : null}
                                                attribute={item.hasOwnProperty(key) ? null : key}
                                                showValue={true}
                                                showUnit={true}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )) : (<TableShimmer columns={columns.length + 2} startAction/>)}
                        </TableBody>
                    </Table>
                    <TablePagination
                    sx={phone ? { position: 'sticky', bottom: 0, backgroundColor: 'Background' } : {}}
                    labelRowsPerPage={t('rowsPerPageLabel')}
                    rowsPerPageOptions={[15, 30, 60]}
                    component="div"
                    count={items.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                </div>
            </div>
        </PageLayout>
    );
};

export default RouteReportPage;
